import Vue from "vue";
import Vuex from "vuex";
import axios from "axios";
import debounce from "lodash/debounce";
import { isLocker, getLockerNatures } from "../utils/amazonLocker";

Vue.use(Vuex);

// Valeur envoyée par la page d'envoi quand aucun tarif ne correspond: aucune recherche à faire
const NO_COMPATIBLE_NATURE = "-";

async function fetchParcelshops(params) {
  if (params.naturesAllowed === NO_COMPATIBLE_NATURE) {
    return [];
  }
  const res = await axios.get("/api/parcelshop", { params });
  return res.data;
}

/**
 * Natures à envoyer à l'API de recherche, au format "1,A,C".
 * Priorité aux natures du tarif choisi (allowedRelayPoints, séparées par ";"), puis aux natures autorisées de la page.
 * Une valeur vide est ignorée (sinon l'API renvoie toutes les natures).
 * Selon le mode de livraison (locker / pudo), seules les natures correspondantes sont envoyées:
 * l'API renvoie un nombre limité de points, un filtre appliqué après coup laisserait trop peu de résultats.
 */
function resolveNaturesAllowed(state, country) {
  const natures = [
    state.params.allowedRelayPoints,
    state.authorizedNatures,
    state.params.parcelshopNatureAllowed,
  ].find((n) => n && n.trim() !== "");

  if (!natures) {
    return "";
  }

  const list = natures
    .split(/[;,]/)
    .map((n) => n.trim())
    .filter((n) => n !== "");

  const lockerNatures = getLockerNatures(country);
  let filtered = list;
  switch (state.params.typeLivraisonFilter) {
    case "locker":
      filtered = list.filter((n) => lockerNatures.includes(n));
      break;
    case "pudo":
      filtered = list.filter((n) => !lockerNatures.includes(n));
      break;
  }

  // Aucune nature compatible avec le mode choisi: pas de recherche
  return filtered.length > 0 || list.length === 0
    ? filtered.join(",")
    : NO_COMPATIBLE_NATURE;
}

Date.prototype.addDays = function (days) {
  var date = new Date(this.valueOf());
  date.setDate(date.getDate() + days);
  return date;
};

/**
 * Calculer l'indisponibilité réelle du relais à présenter au client final, et ajouter les propriétés utiles au parcelshop.
 * Cette fonction utilise les règles définies des -10 jours (#1112) avant date de début et -3 jours avant date de fin de congés:
 * * parcelshop.isUnavailable est un booléen indiquant si le parcelshop est indisponible;
 * * parcelshop.unavailability: contient les dates de début (from) et date de fin (to) des congés à présenter.
 * @param {Parcelshop} parcelshop Le point relais
 * @param {boolean} enableUnavailability Est-ce que la disponibilité est activée sur les infos Point Relais?
 */
function computeUnavailability(parcelshop, enableUnavailability) {
  if (parcelshop.Conges) {
    let now = Date.now();
    let periods = parcelshop.Conges.map((period) => {
      return {
        from: new Date(period.Debut),
        to: new Date(period.Fin),
      };
    })
      .filter((period) => {
        return period.from.addDays(-10) <= now && now <= period.to;
      })
      .sort((period1, period2) => {
        return period1.to < period2.to;
      });
    if (periods && periods.length) {
      parcelshop.isUnavailable =
        enableUnavailability && now <= periods[0].to.addDays(-3);
      parcelshop.unavailability = {
        from: periods[0].from,
        to: periods[0].to,
      };
    }
  }
}

export default new Vuex.Store({
  state: {
    params: {
      isSelectionModeEnabled: false,
      isPriceModeEnabled: false,
      avoidSaturated: false,
      parcelshopNatureAllowed: "",
      parcelshopAgenceAllowed: "",
      typeLivraisonFilter: "",
      allowedRelayPoints: "",
    },
    isLoading: false,
    availableCountries: [],
    userSelectedCountry: "",
    parcelshopList: [],
    _parcelshopListApiRes: [],
    isSearchInitialized: false,
    lastSearch: null,
    focusedParcelshopId: null,
    isPrintInStoreHighlighted: false,
    isCountryPrintInStore: false,
    showFilterPrintInStore: false,
    showFilterLocker: false,
    isLockerHighlighted: false,
    authorizedNatures: "",
    enableUnavailability: false,
    autocomplete: {
      type: "",
      suggestions: [],
    },
    selectedParcelshopId: null,
    isCompleterDonnees: false,
    relayPointsExcluded: [],
  },
  mutations: {
    setAvailableCountries(state, countries) {
      state.availableCountries = countries;
    },
    setUserSelectedCountry(state, country) {
      state.userSelectedCountry = country;
    },
    setParcelshopListApiRes(state, apiRes) {
      if (apiRes) {
        apiRes.map((parcelshop) =>
          computeUnavailability(parcelshop, state.enableUnavailability),
        );
      }
      state._parcelshopListApiRes = apiRes;
    },
    setParcelshopList(state, parcelshopList) {
      parcelshopList.map((parcelshop) =>
        computeUnavailability(parcelshop, state.enableUnavailability),
      );
      state.parcelshopList = parcelshopList;
    },
    setLastSearch(state, lastSearch) {
      state.lastSearch = lastSearch;
    },
    setIsSearchInitialized(state, isSearchInitialized) {
      state.isSearchInitialized = isSearchInitialized;
    },
    setFocusedParcelshopId(state, focusedParcelshopId) {
      state.focusedParcelshopId = focusedParcelshopId;
    },
    setIsPrintInStoreHighlighted(state, isPrintInStoreHighlighted) {
      state.isPrintInStoreHighlighted = isPrintInStoreHighlighted;
    },
    setIsCountryPrintInStore(state, isCountryPrintInStore) {
      state.isCountryPrintInStore = isCountryPrintInStore;
    },
    setAuthorizedNatures(state, authorizedNatures) {
      state.authorizedNatures = authorizedNatures;
    },
    setShowFilterPrintInStore(state, showFilterPrintInStore) {
      state.showFilterPrintInStore = showFilterPrintInStore;
    },
    setShowFilterLocker(state, showFilterLocker) {
      state.showFilterLocker = showFilterLocker;
    },
    setIsLockerHighlighted(state, isLockerHighlighted) {
      state.isLockerHighlighted = isLockerHighlighted;
    },
    setAutocomplete(state, { suggestions, type }) {
      state.autocomplete = { suggestions, type };
    },
    resetAutocomplete(state) {
      state.autocomplete = { type: "", suggestions: [] };
    },
    setSelectedParcelshopId(state, id) {
      state.selectedParcelshopId = id;
    },
    setSelectionMode(state, params) {
      state.params.isSelectionModeEnabled = params;
    },
    setPriceMode(state, params) {
      state.params.isPriceModeEnabled = params;
    },
    setSaturatedParams(state, params) {
      state.params.avoidSaturated = params;
    },
    setTypeLivraisonFilter(state, params) {
      state.params.typeLivraisonFilter = params;
    },
    setAllowedRelayPoints(state, params) {
      state.params.allowedRelayPoints = params;
    },
    setParcelshopNatureAllowed(state, params) {
      state.params.parcelshopNatureAllowed = params;
    },
    setParcelshopAgenceAllowed(state, params) {
      state.params.parcelshopAgenceAllowed = params;
    },
    setLoading(state, isLoading) {
      state.isLoading = isLoading;
    },
    setIsCompleterDonnees(state, isCompleterDonnees) {
      state.isCompleterDonnees = isCompleterDonnees;
    },
    setRelayPointsExcluded(state, relayPoints) {
      state.relayPointsExcluded = relayPoints;
    },
  },
  actions: {
    async fetchCountries(ctx) {
      if (!ctx.isLoading) {
        const res = await axios.get("/api/pays");

        ctx.commit("setAvailableCountries", res.data);
      }
    },
    async findParcelshops(ctx, { zipCode, city, country }) {
      if (!ctx.isLoading) {
        if (country == "" && zipCode == "") {
          return;
        }
        const params = {
          country: country,
          postcode: zipCode,
          city: city,
          services: "", //FIXME: Paramètre sensé être facultatif mais pas pour le moment (#400)
          excludeSat: ctx.state.params.avoidSaturated, // Eviter les points relais saturés
          naturesAllowed: resolveNaturesAllowed(ctx.state, country),
          agencesAllowed: ctx.state.params.parcelshopAgenceAllowed,
        };

        ctx.commit("setLastSearch", { type: "address", zipCode, city, country });
        const data = await fetchParcelshops(params);

        ctx.commit("setParcelshopListApiRes", data);
        ctx.dispatch("getDisplayedParcelDeliveryPoints");
        ctx.commit("setFocusedParcelshopId", null);
        ctx.commit("setIsSearchInitialized", true);
      }
    },
    getDisplayedParcelDeliveryPoints: debounce(function (ctx) {
      const apiResponse = ctx.state._parcelshopListApiRes;
      const allowedParcels = ctx.state.params.allowedRelayPoints;
      const allowedParcelsSet = allowedParcels
        ? new Set(allowedParcels.split(";"))
        : null;

      let parcels;
      switch (ctx.state.params.typeLivraisonFilter) {
        case "locker":
          parcels = apiResponse.filter((i) => isLocker(i));
          break;
        case "pudo":
          parcels = apiResponse.filter((i) => !isLocker(i));
          break;
        default:
          parcels = apiResponse;
          break;
      }

      if (allowedParcels) {
        parcels = parcels.filter((i) => allowedParcelsSet.has(i.CodeNature));
      }

      ctx.commit("setParcelshopList", parcels);
    }, 500),
    async findNearestParcelshops(ctx, { latitude, longitude, country }) {
      const params = {
        country: country,
        postcode: "",
        services: "", //FIXME: Paramètre sensé être facultatif mais pas pour le moment (#400)
        excludeSat: ctx.state.params.avoidSaturated, // Eviter les points relais saturés
        naturesAllowed: resolveNaturesAllowed(ctx.state, country),
        agencesAllowed: ctx.state.params.parcelshopAgenceAllowed,
        latitude,
        longitude,
      };

      ctx.commit("setLastSearch", { type: "nearest", latitude, longitude, country });
      const data = await fetchParcelshops(params);

      // Même chemin que la recherche par adresse pour appliquer les filtres (type de livraison, natures du tarif)
      ctx.commit("setParcelshopListApiRes", data);
      ctx.dispatch("getDisplayedParcelDeliveryPoints");
      ctx.commit("setFocusedParcelshopId", null);
      ctx.commit("setIsSearchInitialized", true);
    },
    // Plusieurs événements de la page d'envoi se suivent au même clic: une seule recherche avec l'état final
    scheduleRefreshSearch: debounce(function (ctx) {
      ctx.dispatch("refreshSearch");
    }, 100),
    async refreshSearch(ctx) {
      const lastSearch = ctx.state.lastSearch;
      if (!lastSearch) {
        return;
      }

      if (lastSearch.type === "nearest") {
        await ctx.dispatch("findNearestParcelshops", lastSearch);
      } else {
        await ctx.dispatch("findParcelshops", lastSearch);
      }
    },
    focusParcelshop(ctx, { parcelshopId }) {
      ctx.commit("setFocusedParcelshopId", parcelshopId);
    },
    async searchForSuggestions(ctx, { type, value }) {
      if (value.length >= 3) {
        let mode = "";

        if (type !== "mobile") {
          mode = type === "city" ? "ville" : "codePostal";
        }

        const params = {
          pays: ctx.state.userSelectedCountry,
          mode,
          term: value,
        };

        const res = await axios.get(
          "/_mvc/fr-FR/Ville/AutocompleteVilleSuggestions",
          { params },
        );

        ctx.commit("setAutocomplete", { suggestions: res.data, type });
      }
    },
  },
  getters: {
    parcelshopList: (state) => {
      if (state.showFilterPrintInStore && state.isPrintInStoreHighlighted) {
        return [...state.parcelshopList].sort((a, b) => {
          return (
            b.Services.some((e) => e.Code === "PRINT") -
            a.Services.some((e) => e.Code === "PRINT")
          );
        });
      }

      if (state.showFilterLocker && state.isLockerHighlighted) {
        return [...state.parcelshopList].sort((a, b) => {
          return (
            (isLocker(b) ? 1 : -1) - (isLocker(a) ? 1 : -1)
          );
        });
      }

      return state.parcelshopList;
    },
  },
});
